<!-- design-lint-disable form-uses-precognition (one- to three-field action modals: Save is the only way out of their last field, and the server answers there under the field) -->
<script>
  import { page, router, useForm } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import { Button } from '../../components/ui'
  import EntityList from '../../components/entity/entity_list.svelte'
  import FilterBar from '../../components/filter_bar.svelte'
  import Paginator from '../../components/paginator.svelte'
  import SearchInput from '../../components/search_input.svelte'
  import FilterCheckbox from '../../components/filter_checkbox.svelte'
  import Modal from '../../components/modal.svelte'
  import Drawer from '../../components/drawer.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import NumberField from '../../components/form/number_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import DateField from '../../components/form/date_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import UserAvatarUploadModal from '../../components/user_avatar_upload_modal.svelte'
  import { confirm } from '../../components/confirm.js'

  // Rows carry their server-computed membership_label, subscription_actions and Stripe URLs.
  // csv_audience arrives only after a CSV import (see importCsv).
  // `can` is the admin's policy answers for this page's actions (UsersController#index).
  let { users = [], pagy, total_count = null, filters = {}, enroll_defaults = {}, default_pause_until, csv_audience = null, can = {} } = $props()

  const canManageUsers = $derived(!!can.manage_users)
  const canManageSubscriptions = $derived(!!can.manage_subscriptions)
  const canAwardPoints = $derived(!!can.award_points)
  const canEditAvatar = $derived(!!can.edit_avatar)
  const areas = $derived($page.props.areas || [])

  const toggleFilter = (key) => {
    const url = new URL($page.url, 'http://placeholder')
    const next = !(filters[key])
    if (next) {
      url.searchParams.set(key, '1')
    } else {
      url.searchParams.delete(key)
    }
    url.searchParams.delete('page')
    router.get(`${url.pathname}${url.search}`, {}, { preserveState: true, preserveScroll: true })
  }

  // -- Profile picture -------------------------------------------------------
  let avatarOpen = $state(false)
  let avatarUser = $state(null)

  const openAvatar = (user) => {
    avatarUser = { id: user.id, name: user.name, icon_image_url: user.icon_image?.image_url ?? null }
    avatarOpen = true
  }

  // -- Award points (single row / college town / CSV) --------------------
  let awardOpen = $state(false)
  let awardMode = $state('user') // 'user' | 'area' | 'csv'
  let awardTargetUser = $state(null)
  let csvError = $state(null)
  let csvImporting = $state(false)
  let audienceDrawerOpen = $state(false)
  let csvFileInput = $state(null)

  // One plain form for all three modes; the server reads `audience` to pick area_id or
  // user_ids, and field + `base` errors land on the open modal.
  const awardForm = useForm({ points: null, message: '', audience: 'area', area_id: '', user_ids: [] })

  const openAward = (mode, fields = {}) => {
    awardMode = mode
    $awardForm.reset()
    $awardForm.clearErrors()
    for (const [key, value] of Object.entries(fields)) $awardForm[key] = value
    awardOpen = true
  }

  const openAwardForUser = (user) => {
    awardTargetUser = user
    openAward('user')
  }

  const openAwardForArea = () => openAward('area', { audience: 'area', area_id: $page.props.current_area_id || '' })

  const openAwardForCsv = () => {
    csvError = null
    openAward('csv', { audience: 'csv' })
  }

  // The server parses the file (Ruby CSV) and answers with the csv_audience prop: every id
  // to award plus the first 100 resolved to name/phone/email for the View Users drawer.
  const importCsv = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    router.post('/users/csv_audience', { csv: file }, {
      only: ['csv_audience'],
      preserveState: true,
      preserveScroll: true,
      preserveUrl: true,
      onStart: () => { csvImporting = true; csvError = null },
      onSuccess: (page) => { $awardForm.user_ids = page.props.csv_audience?.ids ?? [] },
      onError: (errors) => { csvError = errors.csv ?? 'The import failed.' },
      onFinish: () => {
        csvImporting = false
        if (csvFileInput) csvFileInput.value = ''
      }
    })
  }

  const submitAward = (event) => {
    event.preventDefault()
    const url = awardMode === 'user' ? `/users/${awardTargetUser.id}/award_points` : '/users/award_points_csv'
    $awardForm.post(url, {
      preserveScroll: true,
      onSuccess: () => { awardOpen = false }
    })
  }

  // -- Enroll (trial / free) ----------------------------------------------
  let enrollOpen = $state(false)
  let enrollUser = $state(null)
  let enrollType = $state('trial')
  const trialForm = useForm({ days: enroll_defaults.days })
  const freeForm = useForm({ months: enroll_defaults.months })

  const openEnroll = (user) => {
    enrollUser = user
    enrollType = 'trial'
    $trialForm.reset()
    $trialForm.clearErrors()
    $freeForm.reset()
    $freeForm.clearErrors()
    enrollOpen = true
  }

  const submitEnroll = (event) => {
    event.preventDefault()
    if (enrollType === 'trial') {
      $trialForm.patch(`/users/${enrollUser.id}/trial_enroll`, {
        preserveScroll: true,
        onSuccess: () => { enrollOpen = false }
      })
    } else {
      $freeForm.patch(`/users/${enrollUser.id}/free_enroll`, {
        preserveScroll: true,
        onSuccess: () => { enrollOpen = false }
      })
    }
  }

  // -- Pause ----------------------------------------------------------------
  let pauseOpen = $state(false)
  let pauseUser = $state(null)
  const pauseForm = useForm({ pause_until: default_pause_until })

  const openPause = (user) => {
    pauseUser = user
    $pauseForm.reset()
    $pauseForm.clearErrors()
    $pauseForm.pause_until = default_pause_until
    pauseOpen = true
  }

  const submitPause = (event) => {
    event.preventDefault()
    $pauseForm.patch(`/users/${pauseUser.id}/pause`, {
      preserveScroll: true,
      onSuccess: () => { pauseOpen = false }
    })
  }

  // -- Simple confirm-then-post actions -------------------------------------
  const unsubscribeUser = async (user) => {
    const ok = await confirm({
      title: 'Remove Subscription',
      variant: 'error',
      body: 'This will remove a users subscription immediately. Are you sure?'
    })
    if (!ok) return
    router.patch(`/users/${user.id}/reset`, {}, { preserveScroll: true })
  }

  const resumeSubscription = async (user) => {
    const ok = await confirm({
      title: 'Resume Subscription',
      body: 'This will resume a users subscription without their consent. Are you sure?'
    })
    if (!ok) return
    router.patch(`/users/${user.id}/resume`, {}, { preserveScroll: true })
  }

  const cancelSubscription = async (user) => {
    const ok = await confirm({
      title: 'Cancel Subscription',
      variant: 'error',
      body: 'This will cancel a users subscription at their current billing period end. Are you sure?'
    })
    if (!ok) return
    router.patch(`/users/${user.id}/cancel`, {}, { preserveScroll: true })
  }

  const archiveUser = async (user) => {
    const ok = await confirm({
      title: 'Confirm Archive',
      variant: 'error',
      body: 'Archiving is permanent unless reversed by a developer. Are you sure?'
    })
    if (!ok) return
    router.delete(user.url, { preserveScroll: true })
  }

  const areaOptions = $derived(areas.map((area) => ({ value: area.id, label: area.name })))
</script>

<Layout>
  <PageHeader title="Users" subtitle="App members, points, referrals & subscriptions" count={pagy.count} total={total_count}>
    {#snippet actions()}
      {#if canManageUsers}
        <Button href="/users/new" variant="primary" size="sm" icon="add">Create User</Button>
      {/if}
      {#if canAwardPoints}
        <Button variant="default" size="sm" onclick={openAwardForArea}>Award Points to College Town</Button>
        <Button variant="default" size="sm" onclick={openAwardForCsv}>Award Points to CSV</Button>
      {/if}
    {/snippet}
  </PageHeader>

  <FilterBar>
    {#snippet start()}
      <SearchInput value={filters.search || ''} param="search" placeholder="Search users…" />
      <FilterCheckbox label="Referral" checked={filters.referral_program} onchange={() => toggleFilter('referral_program')} />
      <FilterCheckbox label="Influencer" checked={filters.influencer_program} onchange={() => toggleFilter('influencer_program')} />
      <FilterCheckbox label="Quest Curator" checked={filters.quest_curator} onchange={() => toggleFilter('quest_curator')} />
    {/snippet}
  </FilterBar>

  <EntityList entity="user" rows={users} onDelete={canManageUsers ? archiveUser : null} onImageEdit={canEditAvatar ? openAvatar : null}>
    {#snippet actions(user)}
      {#if canAwardPoints}
        <Button variant="ghost" size="xs" square icon="money" title="Award Points" aria-label="Award Points" onclick={() => openAwardForUser(user)} />
      {/if}
      {#if canManageUsers}
        <Button href={user.edit_url} variant="ghost" size="xs" square icon="edit" title="Edit" aria-label="Edit" />
      {/if}
      {#if user.stripe_customer_url}
        <Button href={user.stripe_customer_url} external target="_blank" rel="noreferrer" variant="ghost" size="xs" square icon="credit" title="View customer in Stripe" aria-label="View customer in Stripe" />
      {/if}
      {#if user.stripe_subscription_url}
        <Button href={user.stripe_subscription_url} external target="_blank" rel="noreferrer" variant="ghost" size="xs" square icon="external" title="View subscription in Stripe" aria-label="View subscription in Stripe" />
      {/if}
      {#if canManageSubscriptions}
        {@const can = user.subscription_actions}
        {#if can.enroll}
          <Button variant="ghost" size="xs" title="Enroll" onclick={() => openEnroll(user)}>Enroll</Button>
        {/if}
        {#if can.unsubscribe}
          <Button variant="ghost" size="xs" title="Remove Subscription" onclick={() => unsubscribeUser(user)}>Unsubscribe</Button>
        {/if}
        {#if can.pause}
          <Button variant="ghost" size="xs" title="Pause Subscription" onclick={() => openPause(user)}>Pause</Button>
        {/if}
        {#if can.resume}
          <Button variant="ghost" size="xs" title="Resume Subscription" onclick={() => resumeSubscription(user)}>Resume</Button>
        {/if}
        {#if can.cancel}
          <Button variant="ghost" size="xs" title="Cancel Subscription at Period End" onclick={() => cancelSubscription(user)}>Cancel</Button>
        {/if}
      {/if}
      {#if canManageUsers}
        <Button variant="ghost" size="xs" square icon="delete" title="Archive" aria-label="Archive" class="text-error" onclick={() => archiveUser(user)} />
      {/if}
    {/snippet}
  </EntityList>

  {#if pagy}<Paginator {...pagy} />{/if}

  <UserAvatarUploadModal bind:open={avatarOpen} user={avatarUser} only={['users']} />

  <!-- Award points modal: single-row / college-town-wide / CSV import -->
  <Modal bind:open={awardOpen} title={awardMode === 'user' ? `Award Points to ${awardTargetUser?.name || ''}` : awardMode === 'area' ? 'Award Points to College Town' : 'Award Points to CSV'}>
    <form novalidate onsubmit={submitAward} class="space-y-4">
      {#if awardMode === 'area'}
        <SelectField bind:value={$awardForm.area_id} label="College Town" name="area_id" options={areaOptions} error={$awardForm.errors.area_id} />
      {/if}
      {#if awardMode === 'csv'}
        <div class="flex items-center gap-2">
          <input
            bind:this={csvFileInput}
            type="file"
            accept="text/csv"
            class="hidden"
            id="csv-import"
            onchange={importCsv}
          />
          <Button variant="default" size="sm" loading={csvImporting} onclick={() => csvFileInput?.click()}>Import from CSV</Button>
          <Button variant="default" size="sm" onclick={() => ($awardForm.user_ids = [])}>Clear Users</Button>
          <Button variant="default" size="sm" disabled={$awardForm.user_ids.length === 0} onclick={() => (audienceDrawerOpen = true)}>View Users</Button>
          <span class="text-sm text-base-content/60">{$awardForm.user_ids.length && csv_audience ? csv_audience.summary : '0 users included.'}</span>
        </div>
        {#if csvError}<p class="text-error text-sm">{csvError}</p>{/if}
      {/if}
      {#if awardMode === 'user'}
        <p class="text-sm">Email: {awardTargetUser?.email}</p>
        <p class="text-sm">Phone: {awardTargetUser?.phone}</p>
      {/if}
      <NumberField bind:value={$awardForm.points} label="Points" name="points" error={$awardForm.errors.points} />
      <TextareaField bind:value={$awardForm.message} label="Message" name="message" error={$awardForm.errors.message} />
      {#if $awardForm.errors.base}<p class="text-error text-sm">{$awardForm.errors.base}</p>{/if}
      <SubmitButton processing={$awardForm.processing} label="Award" />
    </form>
  </Modal>

  <Drawer bind:open={audienceDrawerOpen} title="User List" overModal>
    {#if $awardForm.user_ids.length === 0 || !csv_audience}
      <p class="text-sm text-base-content/60">No users imported yet.</p>
    {:else}
      <ul class="space-y-1">
        {#each csv_audience.preview as person (person.id)}
          <li class="text-sm">
            {#if person.found}
              <a href={person.url} class="link link-hover">{person.name || 'Unnamed'}</a>
              <span class="text-base-content/60">/ {person.phone || '—'} / {person.email || '—'} ({person.id})</span>
            {:else}
              <span class="text-error">Not found</span> <span class="text-base-content/60">({person.id})</span>
            {/if}
          </li>
        {/each}
      </ul>
      {#if csv_audience.more_label}
        <p class="text-xs text-base-content/60 mt-2">{csv_audience.more_label}</p>
      {/if}
    {/if}
  </Drawer>

  <!-- Enroll drawer: trial (days) or free (months) -->
  <Drawer bind:open={enrollOpen} title="Enroll User">
    <div class="mb-4">
      <SelectField
        bind:value={enrollType}
        label="Enroll Type"
        name="enroll_type"
        options={[{ value: 'trial', label: 'Trial Enroll' }, { value: 'free', label: 'Free Enroll' }]}
      />
    </div>
    {#if enrollType === 'trial'}
      <form novalidate onsubmit={submitEnroll} class="space-y-4">
        <NumberField bind:value={$trialForm.days} label="Days" name="days" error={$trialForm.errors.days} />
        {#if $trialForm.errors.base}<p class="text-error text-sm">{$trialForm.errors.base}</p>{/if}
        <SubmitButton processing={$trialForm.processing} label="Trial Enroll" />
      </form>
    {:else}
      <form novalidate onsubmit={submitEnroll} class="space-y-4">
        <NumberField bind:value={$freeForm.months} label="Months" name="months" error={$freeForm.errors.months} />
        {#if $freeForm.errors.base}<p class="text-error text-sm">{$freeForm.errors.base}</p>{/if}
        <SubmitButton processing={$freeForm.processing} label="Free Enroll" />
      </form>
    {/if}
  </Drawer>

  <!-- Pause drawer -->
  <Drawer bind:open={pauseOpen} title="Pause Subscription">
    <form novalidate onsubmit={submitPause} class="space-y-4">
      <DateField bind:value={$pauseForm.pause_until} label="Pause Until" name="pause_until" error={$pauseForm.errors.pause_until} />
      {#if $pauseForm.errors.base}<p class="text-error text-sm">{$pauseForm.errors.base}</p>{/if}
      <SubmitButton processing={$pauseForm.processing} label="Pause" />
    </form>
  </Drawer>
</Layout>
